import { decodeEntities, escapeHtml, type HtmlAttr } from "./simpleHtml";
import { SIGNATURE_BLOCKS, SIGNATURE_VOID, signaturePieces } from "./signatureHtml";

/** How a signature's simple HTML is filled in (the signature HTML and role round, 2026-10-28; brief rule 2 and "After the red team's review",
 *  findings 1 to 5). The steps, each its own function:
 *  1. read: the pieces the short list keeps (signatureHtml.ts), text decoded, every line break marked;
 *  2. breaks: a line break counts (`<br>`) unless it sits next to a block tag, whitespace apart; then it is only space;
 *  3. placeholders: filled in text only, never inside a tag; the logo is the filler's own picture;
 *  4. lines: a line that held a placeholder and came out empty goes (its own tags with it); empty lines at both ends go;
 *  5. tags balanced: a stray closing tag goes, an open one is closed, a paragraph, item, row or cell closes the one before;
 *  6. blocks: a `<p>`, `<li>` or `<tr>` that held a placeholder and came out empty goes;
 *  7. written out: every text escaped, every tag written by the filler with the attributes it keeps (the link colour on links). */

export type FillLogo = { html: (width: string | undefined, height: string | undefined) => string } | null;
export type FillInput = { values: Record<string, string>; logo: FillLogo; linkColor: string | null };

type Tag = { k: "open" | "close" | "void"; name: string; attrs: HtmlAttr[] };
type Item = Tag | { k: "text"; s: string } | { k: "value"; s: string } | { k: "logo"; html: string } | { k: "nl" };

/** the placeholders a signature fills; the logo may carry a width (`{logo 160}`) or a width and height (`{logo 160x60}`) */
export const SIGNATURE_PLACEHOLDER = /\{(?:(firstname|lastname|name|role|project|workspace)|logo(?: (\d{1,4})(?:x(\d{1,4}))?)?)\}/g;

/** the filled signature as HTML (no wrapper, no colour on the text: the caller adds those) */
export function fillSignatureHtml(template: string, input: FillInput): string {
  const read = withPlaceholders(withBreaks(readItems(template)), input);
  const kept = blocksDropped(balanced(linesDropped(read)));
  return kept.map((i) => written(i, input.linkColor)).join("");
}

/** step 1: the kept pieces, text decoded, every new line its own item (classified in step 2) */
function readItems(template: string): (Item | { k: "raw-nl" })[] {
  const out: (Item | { k: "raw-nl" })[] = [];
  for (const p of signaturePieces(template.replace(/\r\n?/g, "\n"))) {
    if (p.kind === "tag") { out.push({ k: SIGNATURE_VOID.has(p.name) ? "void" : p.close ? "close" : "open", name: p.name, attrs: p.attrs }); continue; }
    decodeEntities(p.raw).split("\n").forEach((s, i) => { if (i > 0) out.push({ k: "raw-nl" }); if (s) out.push({ k: "text", s }); });
  }
  return out;
}

const isBlank = (i: Item | { k: "raw-nl" }) => i.k === "raw-nl" || (i.k === "text" && !i.s.trim());
const isBlockTag = (i: Item | { k: "raw-nl" } | undefined) => !!i && (i.k === "open" || i.k === "close" || i.k === "void") && SIGNATURE_BLOCKS.has(i.name);

/** step 2: a new line next to a block tag (whitespace apart) is space, every other one a line break */
function withBreaks(items: (Item | { k: "raw-nl" })[]): Item[] {
  const near = (from: number, step: 1 | -1) => { let j = from + step; while (j >= 0 && j < items.length && isBlank(items[j]!)) j += step; return items[j]; };
  return items.map((i, at) => (i.k !== "raw-nl" ? i : isBlockTag(near(at, -1)) || isBlockTag(near(at, 1)) ? { k: "text", s: "\n" } : { k: "nl" }));
}

/** step 3: the placeholders filled in the text items, the logo drawn by the filler */
function withPlaceholders(items: Item[], input: FillInput): Item[] {
  return items.flatMap((i): Item[] => {
    if (i.k !== "text") return [i];
    const out: Item[] = []; let at = 0;
    for (const m of i.s.matchAll(SIGNATURE_PLACEHOLDER)) {
      if (m.index! > at) out.push({ k: "text", s: i.s.slice(at, m.index) });
      out.push(m[1] === undefined ? { k: "logo", html: input.logo?.html(m[2], m[3]) ?? "" } : { k: "value", s: input.values[m[1]] ?? "" });
      at = m.index! + m[0].length;
    }
    if (at < i.s.length) out.push({ k: "text", s: i.s.slice(at) });
    return out;
  });
}

const held = (items: Item[]) => items.some((i) => i.k === "value" || i.k === "logo");
const hasContent = (items: Item[]) => items.some((i) => ((i.k === "text" || i.k === "value") && i.s.trim() !== "") || (i.k === "logo" && i.html !== "") || i.k === "void");
const isTag = (i: Item): i is Tag => i.k === "open" || i.k === "close" || i.k === "void";

/** step 4: the line rule of before, for tags too: an emptied line goes; the tags it shares with other lines move to its neighbours */
function linesDropped(items: Item[]): Item[] {
  const lines: Item[][] = [[]];
  for (const i of items) { if (i.k === "nl") lines.push([]); else lines[lines.length - 1]!.push(i); }
  const kept: Item[][] = []; let carried: Item[] = [];
  for (const l of lines) {
    if (!(held(l) && !hasContent(l))) { kept.push([...carried, ...trimmedEnd(l)]); carried = []; continue; }
    const tags = unmatchedTags(l);
    const closes = tags.filter((t) => t.k === "close"), opens = tags.filter((t) => t.k !== "close");
    if (kept.length) kept[kept.length - 1]!.push(...closes); else carried.push(...closes);
    carried.push(...opens);
  }
  if (carried.length) { if (kept.length) kept[kept.length - 1]!.push(...carried); else kept.push(carried); }
  while (kept.length && isEmptyLine(kept[0]!)) kept.shift();
  while (kept.length && isEmptyLine(kept[kept.length - 1]!)) kept.pop();
  return kept.flatMap((l, n) => (n === 0 ? l : [{ k: "nl" } as Item, ...l]));
}
const isEmptyLine = (l: Item[]) => !hasContent(l) && !l.some(isTag);

/** the tags of a dropped line that open or close something outside it (a pair inside the line goes with the line) */
function unmatchedTags(line: Item[]): Tag[] {
  const tags = line.filter(isTag), gone = new Set<number>(), open: number[] = [];
  tags.forEach((t, n) => {
    if (t.k === "void") { gone.add(n); return; }
    if (t.k === "open") { open.push(n); return; }
    const match = [...open].reverse().find((o) => tags[o]!.name === t.name);
    if (match !== undefined) { gone.add(match); gone.add(n); open.splice(open.lastIndexOf(match), 1); }
  });
  return tags.filter((_, n) => !gone.has(n));
}

/** a line loses its trailing spaces ("Mike {lastname}" reads "Mike") */
function trimmedEnd(line: Item[]): Item[] {
  const out = [...line];
  for (let n = out.length - 1; n >= 0; n--) {
    const i = out[n]!;
    if (i.k !== "text" && i.k !== "value") break;
    const s = i.s.replace(/\s+$/, "");
    out[n] = { ...i, s };
    if (s) break;
  }
  return out;
}

/** a tag that closes the open one before it (a paragraph a paragraph, an item an item, a row a row and its cell, a cell a cell) */
const CLOSES: Record<string, string[]> = { p: ["p"], li: ["li", "p"], tr: ["td", "th", "tr", "p"], td: ["td", "th", "p"], th: ["td", "th", "p"], ul: ["p"], ol: ["p"], table: ["p"], hr: ["p"] };

/** step 5: every tag closed in order; a closing tag with nothing open of its name goes */
function balanced(items: Item[]): Item[] {
  const out: Item[] = [], stack: string[] = [];
  const close = () => out.push({ k: "close", name: stack.pop()!, attrs: [] });
  for (const i of items) {
    if (!isTag(i)) { out.push(i); continue; }
    if (i.k !== "close") { while (stack.length && (CLOSES[i.name] ?? []).includes(stack[stack.length - 1]!)) close(); }
    if (i.k === "close") { if (!stack.includes(i.name)) continue; while (stack[stack.length - 1] !== i.name) close(); close(); continue; }
    out.push(i);
    if (i.k === "open") stack.push(i.name);
  }
  while (stack.length) close();
  return out;
}

/** step 6: an emptied `<p>`, `<li>` or `<tr>` (it held a placeholder, and no words or picture are left) goes with what it holds */
function blocksDropped(items: Item[]): Item[] {
  const out: Item[] = [];
  for (let n = 0; n < items.length; n++) {
    const i = items[n]!;
    if (i.k === "open" && (i.name === "p" || i.name === "li" || i.name === "tr")) {
      const end = matchingClose(items, n);
      const inner = items.slice(n + 1, end);
      if (held(inner) && !hasContent(inner)) { n = end; continue; }
    }
    out.push(i);
  }
  return out;
}
function matchingClose(items: Item[], from: number): number {
  const name = (items[from] as Tag).name; let depth = 0;
  for (let n = from; n < items.length; n++) {
    const i = items[n]!;
    if (i.k === "open" && i.name === name) depth++;
    if (i.k === "close" && i.name === name && --depth === 0) return n;
  }
  return items.length - 1;
}

/** step 7: one item as HTML */
function written(i: Item, linkColor: string | null): string {
  switch (i.k) {
    case "text": case "value": return escapeHtml(i.s);
    case "logo": return i.html;
    case "nl": return "<br>";
    case "close": return `</${i.name}>`;
    default: {
      const attrs = i.attrs.map((a) => ` ${a.name}="${escapeHtml(a.value)}"`).join("");
      const style = i.name === "a" && linkColor ? ` style="color:${linkColor}"` : "";
      return `<${i.name}${attrs}${style}>`;
    }
  }
}
